// eslint-disable-next-line import/no-extraneous-dependencies
import dagre from '@dagrejs/dagre';
import { type Edge, type Node, Position } from '@xyflow/react';
import { type BaseNodeData } from '../types';
import { hasEstimatedHeight, isSwitchStepNode } from '../types/typeGuards';
import {
	NODE_WIDTH,
	BRANCH_VERTICAL_GAP,
	INTRA_BRANCH_VERTICAL_GAP,
	BRANCH_MARGIN,
	SEGMENT_NODE,
	STEP_NODE_COMPACT_HEIGHT,
	TRIGGER_NODE,
} from './layoutConstants';

// Vertical layout configuration
const VERTICAL_LAYOUT_CONFIG = {
	direction: 'TB' as const,
	nodeSpacing: 40, // Horizontal spacing between sibling nodes (40px margin)
	rankSpacing: 80, // Vertical spacing between ranks
};

const DEFAULT_NODE_HEIGHT = 120;

// Maximum expected height for a compact (collapsed) node.
// If measured.height exceeds this after a collapse, it is stale from the
// previous expansion and must be ignored to avoid positioning downstream
// nodes as though the expanded card is still present.
const MAX_COMPACT_NODE_HEIGHT = 200;

function getNodeHeight(node: Node<BaseNodeData>): number {
	const measuredHeight = typeof node.measured?.height === 'number' ? node.measured.height : null;
	const estimatedHeight = hasEstimatedHeight(node.data) ? node.data.estimatedHeight : null;
	const isExpanded = (node.data as Record<string, unknown>)?.isExpanded === true;

	if (isExpanded) {
		// When expanded, prefer the larger of measured and estimated heights.
		// React Flow's measured.height can be stale (reflecting the collapsed size)
		// when a node has just expanded and the component's ResizeObserver has
		// already captured the true expanded height in estimatedHeight.
		if (measuredHeight !== null && estimatedHeight !== null) {
			return Math.max(measuredHeight, estimatedHeight);
		}
		if (estimatedHeight !== null) {
			return estimatedHeight;
		}
		if (measuredHeight !== null) {
			return measuredHeight;
		}
	} else {
		// When collapsed, measured/height values can lag behind and still reflect
		// expanded dimensions for one or more frames. Never trust compact heights
		// above MAX_COMPACT_NODE_HEIGHT.
		if (measuredHeight !== null && measuredHeight <= MAX_COMPACT_NODE_HEIGHT) {
			return measuredHeight;
		}
		if (estimatedHeight !== null && estimatedHeight <= MAX_COMPACT_NODE_HEIGHT) {
			return estimatedHeight;
		}

		if (node.type === 'segment') {
			return SEGMENT_NODE.COLLAPSED_HEIGHT;
		}
		if (node.type !== 'trigger' && node.type !== 'phantomTarget') {
			return STEP_NODE_COMPACT_HEIGHT;
		}
	}

	if (typeof node.height === 'number') {
		return node.height;
	}

	if (estimatedHeight !== null) {
		return estimatedHeight;
	}

	if (node.type === 'segment') {
		return SEGMENT_NODE.MIN_HEIGHT;
	}

	if (node.type === 'trigger') {
		return TRIGGER_NODE.HEIGHT;
	}

	return DEFAULT_NODE_HEIGHT;
}

/**
 * Gets the visual/stacking height for a node.
 * Used for vertical stacking within branches where we need the actual rendered height.
 * Delegates to getNodeHeight which handles the measured vs estimated height priority.
 */
function getNodeStackingHeight(node: Node<BaseNodeData>): number {
	return getNodeHeight(node);
}

/**
 * Gets the actual width for a node based on its type.
 * All visible nodes use a consistent width for alignment:
 * - Segment nodes: 560px
 * - Trigger nodes: 560px
 * - Step nodes: 560px
 * - Phantom nodes: 1px (invisible drop targets)
 */
function getNodeWidth(node: Node<BaseNodeData>): number {
	if (node.type === 'segment') {
		return SEGMENT_NODE.WIDTH;
	}
	if (node.type === 'trigger') {
		return TRIGGER_NODE.WIDTH;
	}
	if (node.type === 'phantomTarget') {
		return 1;
	}
	return NODE_WIDTH;
}

/**
 * Calculates uniform lane width based on the maximum node width across all branches.
 * This creates visually consistent columns even when branches contain different node types.
 *
 * @param branches - Map of branch key to node IDs in that branch
 * @param nodes - All nodes in the flow
 * @returns Maximum width of any node across all branches
 */
function getUniformLaneWidth(branches: Map<string, string[]>, nodes: Node<BaseNodeData>[]): number {
	let maxWidth: number = SEGMENT_NODE.WIDTH; // Minimum lane width
	for (const branchNodeIds of branches.values()) {
		for (const nodeId of branchNodeIds) {
			const node = nodes.find(n => n.id === nodeId);
			if (node) {
				maxWidth = Math.max(maxWidth, getNodeWidth(node));
			}
		}
	}
	return maxWidth;
}

export function applyLayout(
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	const config = VERTICAL_LAYOUT_CONFIG;
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));

	dagreGraph.setGraph({
		rankdir: config.direction,
		nodesep: config.nodeSpacing,
		ranksep: config.rankSpacing,
		ranker: 'network-simplex',
	});

	nodes.forEach(node => {
		dagreGraph.setNode(node.id, {
			width: getNodeWidth(node),
			height: getNodeHeight(node),
		});
	});

	edges.forEach(edge => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	const layoutedNodesWithPositions: Node<BaseNodeData>[] = nodes.map(node => {
		const nodeWithPosition = dagreGraph.node(node.id);
		const nodeHeight = getNodeHeight(node);
		const nodeWidth = getNodeWidth(node);

		return {
			...node,
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			data: {
				...node.data,
				targetPosition: Position.Top,
				sourcePosition: Position.Bottom,
			},
			position: {
				// Dagre returns center coordinates, convert to top-left
				x: nodeWithPosition.x - nodeWidth / 2,
				y: nodeWithPosition.y - nodeHeight / 2,
			},
		};
	});

	const layoutedNodes = applySwitchBranchLayout(layoutedNodesWithPositions, edges);

	return { nodes: layoutedNodes, edges };
}

/**
 * Represents metadata about a switch node and its branches
 */
interface SwitchNodeMetadata {
	switchNodeId: string;
	branches: Map<string, string[]>; // handleId -> array of node IDs in that branch
}

/**
 * Identifies switch nodes and their branches in the flow.
 * Returns metadata about each switch node including which nodes belong to which branch.
 *
 * Since switch nodes are now hidden, branches are identified by checking edge data
 * for the `switchNodeId` property, not by checking the edge source.
 *
 * @param nodes - All nodes in the flow
 * @param edges - All edges in the flow
 * @returns Array of switch node metadata
 */
function identifySwitchBranches(nodes: Node<BaseNodeData>[], edges: Edge[]): SwitchNodeMetadata[] {
	const switchMetadata: SwitchNodeMetadata[] = [];

	// Find all switch nodes (including hidden ones)
	const switchNodes = nodes.filter(node => isSwitchStepNode(node.data));

	for (const switchNode of switchNodes) {
		const branches = new Map<string, string[]>();

		// Find all branch edges belonging to this switch node.
		// Since switch nodes are hidden, edges now originate from the parent step
		// but contain `switchNodeId` in their data to identify the owning switch.
		const branchEdges = edges.filter(edge => {
			if (edge.type !== 'branch') return false;
			const edgeData = edge.data as { switchNodeId?: string } | undefined;
			// Check if edge belongs to this switch (via switchNodeId in data)
			// or if it's an old-style edge directly from the switch node
			return edgeData?.switchNodeId === switchNode.id || edge.source === switchNode.id;
		});

		// Sort by caseIndex from edge data for consistent left-to-right ordering
		const sortedBranchEdges = [...branchEdges].sort((a, b) => {
			const aIndex = (a.data as { caseIndex?: number })?.caseIndex ?? 0;
			const bIndex = (b.data as { caseIndex?: number })?.caseIndex ?? 0;
			return aIndex - bIndex;
		});

		// Process each branch edge
		for (const branchEdge of sortedBranchEdges) {
			const caseIndex = (branchEdge.data as { caseIndex?: number })?.caseIndex ?? 0;
			const branchKey = `branch-${caseIndex}`;
			const branchNodeIds: string[] = [];
			const visited = new Set<string>();
			const queue: string[] = [branchEdge.target];
			visited.add(branchEdge.target);

			// Find all descendants using BFS
			while (queue.length > 0) {
				const currentNodeId = queue.shift()!;
				branchNodeIds.push(currentNodeId);

				// Find children of current node (but stop at other switch nodes)
				const currentNode = nodes.find(n => n.id === currentNodeId);
				if (!currentNode) {
					continue;
				}

				const isAnotherSwitch = isSwitchStepNode(currentNode.data);

				if (!isAnotherSwitch) {
					const childEdges = edges.filter(edge => edge.source === currentNodeId);
					for (const edge of childEdges) {
						if (!visited.has(edge.target)) {
							visited.add(edge.target);
							queue.push(edge.target);
						}
					}
				}
			}

			branches.set(branchKey, branchNodeIds);
		}

		switchMetadata.push({
			switchNodeId: switchNode.id,
			branches,
		});
	}

	return switchMetadata;
}

/**
 * Builds an ordered chain of nodes within a branch for vertical stacking.
 * Uses edge relationships to determine the order from parent to leaf.
 *
 * @param branchNodeIds - All node IDs in the branch
 * @param edges - All edges in the flow
 * @param directChildIds - Direct children of the switch node for this branch
 * @returns Ordered array of node IDs from top to bottom
 */
function buildOrderedBranchChain(branchNodeIds: string[], edges: Edge[], directChildIds: string[]): string[] {
	if (branchNodeIds.length === 0) {
		return [];
	}

	// Build adjacency map for nodes within this branch
	const childMap = new Map<string, string[]>();
	const branchNodeSet = new Set(branchNodeIds);

	for (const edge of edges) {
		if (branchNodeSet.has(edge.source) && branchNodeSet.has(edge.target)) {
			const children = childMap.get(edge.source) || [];
			children.push(edge.target);
			childMap.set(edge.source, children);
		}
	}

	// BFS from direct children to build ordered list
	const ordered: string[] = [];
	const visited = new Set<string>();
	const queue: string[] = [...directChildIds];

	while (queue.length > 0) {
		const nodeId = queue.shift()!;
		if (visited.has(nodeId)) {
			continue;
		}
		visited.add(nodeId);
		ordered.push(nodeId);

		// Add children to queue (maintains topological order)
		const children = childMap.get(nodeId) || [];
		for (const childId of children) {
			if (!visited.has(childId)) {
				queue.push(childId);
			}
		}
	}

	// Add any remaining nodes that weren't reachable from direct children
	// (shouldn't happen in well-formed flows, but defensive coding)
	for (const nodeId of branchNodeIds) {
		if (!visited.has(nodeId)) {
			ordered.push(nodeId);
		}
	}

	return ordered;
}

/**
 * Applies lane-based layout for switch node branches in vertical layout.
 * Each branch gets its own horizontal lane (column), positioned left-to-right
 * based on the handle index order (handle 0 = leftmost, handle N = rightmost).
 *
 * This function runs after Dagre layout and repositions branch nodes to:
 * 1. Ensure left-to-right ordering matches case index order
 * 2. Center branches around the parent switch node
 * 3. Maintain consistent vertical spacing for clean edge paths
 *
 * @param nodes - All nodes in the flow
 * @param edges - All edges in the flow
 * @returns Nodes with updated positions for lane-based layout
 */
export function applySwitchBranchLayout(nodes: Node<BaseNodeData>[], edges: Edge[]): Node<BaseNodeData>[] {
	// Defensive check: ensure inputs are valid
	if (!nodes || nodes.length === 0) {
		return nodes;
	}

	if (!edges || edges.length === 0) {
		return nodes;
	}

	const switchMetadata = identifySwitchBranches(nodes, edges);

	if (switchMetadata.length === 0) {
		return nodes;
	}

	const updatedNodes = [...nodes];
	const nodeIndexMap = new Map<string, number>();
	updatedNodes.forEach((node, index) => {
		nodeIndexMap.set(node.id, index);
	});
	const positionedNodes = new Set<string>();

	const getNodeById = (nodeId: string) => {
		const nodeIndex = nodeIndexMap.get(nodeId);
		if (nodeIndex === undefined) {
			return undefined;
		}
		return updatedNodes[nodeIndex];
	};

	const updateNodePosition = (nodeId: string, newPosition: { x: number; y: number }) => {
		const nodeIndex = nodeIndexMap.get(nodeId);
		if (nodeIndex === undefined) {
			return;
		}
		const node = updatedNodes[nodeIndex];
		updatedNodes[nodeIndex] = {
			...node,
			position: newPosition,
		};
	};

	for (const metadata of switchMetadata) {
		const switchNodeIndex = updatedNodes.findIndex(n => n.id === metadata.switchNodeId);
		if (switchNodeIndex === -1) {
			continue;
		}

		const switchNode = updatedNodes[switchNodeIndex];

		// Defensive check: ensure switch node has valid position
		if (
			!switchNode.position ||
			typeof switchNode.position.x !== 'number' ||
			typeof switchNode.position.y !== 'number'
		) {
			continue;
		}

		// Get branch count from metadata (identified by edge caseIndex)
		const branchCount = metadata.branches.size;
		if (branchCount === 0) {
			continue;
		}

		updatedNodes[switchNodeIndex] = {
			...switchNode,
			sourcePosition: Position.Bottom,
			data: {
				...switchNode.data,
				sourcePosition: Position.Bottom,
			},
		};

		// Find the visual parent node (the step that branches connect FROM)
		// The switch node is hidden, so we need to position relative to the actual visual parent
		const branchEdge = edges.find(e => {
			const data = e.data as { switchNodeId?: string };
			return data?.switchNodeId === metadata.switchNodeId;
		});
		const visualParentId = branchEdge?.source;
		const visualParentNode = visualParentId ? getNodeById(visualParentId) : null;

		// Use visual parent for positioning, fall back to switch node if not found
		const parentForPositioning = visualParentNode || switchNode;
		const parentHeight = getNodeHeight(parentForPositioning);
		const parentWidth = getNodeWidth(parentForPositioning);
		const parentCenterX = parentForPositioning.position.x + parentWidth / 2;

		// Calculate uniform lane width based on the largest node across all branches
		// This creates visually consistent columns even when branches contain different node types
		const uniformLaneWidth = getUniformLaneWidth(metadata.branches, updatedNodes);
		const branchSpacing = uniformLaneWidth + BRANCH_MARGIN;

		// Calculate total width and starting position to center branches around visual parent
		const totalWidth = (branchCount - 1) * branchSpacing;
		const startLaneX = parentCenterX - totalWidth / 2;

		// Target Y for first branch nodes: consistent gap below visual parent (not hidden switch)
		const targetBranchY = parentForPositioning.position.y + parentHeight + BRANCH_VERTICAL_GAP;

		// Get sorted branch keys for left-to-right ordering
		const sortedBranchKeys = Array.from(metadata.branches.keys()).sort((a, b) => {
			const aIndex = parseInt(a.replace('branch-', ''), 10) || 0;
			const bIndex = parseInt(b.replace('branch-', ''), 10) || 0;
			return aIndex - bIndex;
		});

		const branchLanes: { laneCenterX: number; orderedNodeIds: string[] }[] = [];

		// Build ordered node chains for each branch in caseIndex order (left to right)
		for (let branchIndex = 0; branchIndex < sortedBranchKeys.length; branchIndex++) {
			const branchKey = sortedBranchKeys[branchIndex];
			const branchNodeIds = metadata.branches.get(branchKey);

			if (!branchNodeIds || branchNodeIds.length === 0) {
				continue;
			}

			// Calculate lane center X for this branch
			const laneCenterX = startLaneX + branchIndex * branchSpacing;

			// First node in the branch is the direct child of the switch
			const directChildIds = [branchNodeIds[0]];

			// Build ordered chain of nodes for vertical stacking
			const orderedNodeIds = buildOrderedBranchChain(branchNodeIds, edges, directChildIds);

			branchLanes.push({ laneCenterX, orderedNodeIds });
		}

		const maxDepth = branchLanes.reduce((depth, lane) => Math.max(depth, lane.orderedNodeIds.length), 0);
		const rowHeights: number[] = [];

		for (let depth = 0; depth < maxDepth; depth++) {
			let maxHeight = 0;
			for (const lane of branchLanes) {
				const nodeId = lane.orderedNodeIds[depth];
				if (!nodeId) {
					continue;
				}

				const branchNode = getNodeById(nodeId);
				if (!branchNode) {
					continue;
				}

				maxHeight = Math.max(maxHeight, getNodeStackingHeight(branchNode));
			}

			rowHeights.push(maxHeight || DEFAULT_NODE_HEIGHT);
		}

		let currentRowY = targetBranchY;
		const rowYPositions = rowHeights.map(height => {
			const rowY = currentRowY;
			currentRowY += height + INTRA_BRANCH_VERTICAL_GAP;
			return rowY;
		});

		// Position each node within its lane while keeping rows aligned across branches
		for (const lane of branchLanes) {
			for (let depth = 0; depth < lane.orderedNodeIds.length; depth++) {
				const nodeId = lane.orderedNodeIds[depth];
				if (positionedNodes.has(nodeId)) {
					continue;
				}

				const branchNode = getNodeById(nodeId);
				if (!branchNode) {
					continue;
				}

				const nodeWidth = getNodeWidth(branchNode);
				const nodeX = lane.laneCenterX - nodeWidth / 2;
				const nodeY = rowYPositions[depth] ?? targetBranchY;

				updateNodePosition(nodeId, {
					x: nodeX,
					y: nodeY,
				});

				positionedNodes.add(nodeId);
			}
		}

		// Reposition phantom target nodes for this switch
		// Phantom targets should be positioned to the right of the rightmost (default) segment
		const phantomTargetId = `phantom-target-${metadata.switchNodeId}`;
		const phantomTargetIndex = nodeIndexMap.get(phantomTargetId);
		if (phantomTargetIndex !== undefined) {
			// Find the rightmost segment position (last branch, which is the default)
			const lastBranchKey = sortedBranchKeys[sortedBranchKeys.length - 1];
			const lastBranchNodeIds = metadata.branches.get(lastBranchKey);
			if (lastBranchNodeIds && lastBranchNodeIds.length > 0) {
				const lastSegmentNode = getNodeById(lastBranchNodeIds[0]);
				if (lastSegmentNode) {
					// Position phantom target to the right of the rightmost segment
					// Use SEGMENT_NODE.WIDTH since segments are narrower than regular nodes
					const phantomX = lastSegmentNode.position.x + SEGMENT_NODE.WIDTH + 80;
					updateNodePosition(phantomTargetId, {
						x: phantomX,
						y: lastSegmentNode.position.y,
					});
				}
			}
		}
	}

	// Build exclusion set: branch-positioned nodes + hidden nodes + phantom targets
	// These nodes are already correctly positioned by applySwitchBranchLayout
	// and should not be displaced by the generic collision resolver.
	const excludeFromCollision = new Set(positionedNodes);
	for (const node of updatedNodes) {
		if (node.hidden || node.type === 'phantomTarget') {
			excludeFromCollision.add(node.id);
		}
	}

	// Detect and resolve any remaining collisions
	// Pass edges to skip collision detection between directly connected nodes
	const finalNodes = detectAndResolveCollisions(updatedNodes, edges, excludeFromCollision);

	return finalNodes;
}

/**
 * Represents a bounding box for collision detection
 */
interface BoundingBox {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

/**
 * Gets the bounding box for a node
 * Returns null if node has invalid position
 */
function getNodeBoundingBox(node: Node<BaseNodeData>): BoundingBox | null {
	// Handle nodes with invalid positions
	if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
		return null;
	}

	const width = getNodeWidth(node);
	const height = getNodeHeight(node);
	return {
		left: node.position.x,
		right: node.position.x + width,
		top: node.position.y,
		bottom: node.position.y + height,
	};
}

/**
 * Checks if two bounding boxes overlap
 */
function boxesOverlap(a: BoundingBox, b: BoundingBox): boolean {
	return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * Checks if two nodes are directly connected by an edge
 */
function areNodesConnected(nodeIdA: string, nodeIdB: string, connectedPairs: Set<string>): boolean {
	return connectedPairs.has(`${nodeIdA}-${nodeIdB}`) || connectedPairs.has(`${nodeIdB}-${nodeIdA}`);
}

/**
 * Detects overlapping nodes and adjusts positions to resolve collisions.
 * Uses a simple push-apart strategy: if two nodes overlap horizontally
 * (in different branches), push the second one down.
 *
 * Skips collision detection for directly connected nodes, as they are
 * expected to be vertically adjacent in the same chain.
 *
 * @param nodes - Nodes to check for collisions
 * @param edges - Edges connecting nodes (to skip connected pairs)
 * @returns Nodes with adjusted positions to prevent overlaps
 */
function detectAndResolveCollisions(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	excludeFromCollision?: Set<string>
): Node<BaseNodeData>[] {
	if (nodes.length < 2) {
		return nodes;
	}

	// Build a set of connected node pairs for fast lookup
	const connectedPairs = new Set<string>();
	for (const edge of edges) {
		connectedPairs.add(`${edge.source}-${edge.target}`);
	}

	const updatedNodes = [...nodes];
	const collisionMargin = 20; // Minimum gap between nodes
	let hasCollisions = true;
	let iterations = 0;
	const maxIterations = 10; // Prevent infinite loops

	while (hasCollisions && iterations < maxIterations) {
		hasCollisions = false;
		iterations++;

		for (let i = 0; i < updatedNodes.length; i++) {
			const nodeA = updatedNodes[i];

			// Skip excluded nodes (branch-positioned, hidden, phantom targets).
			// These are already correctly positioned and must not be displaced.
			if (excludeFromCollision?.has(nodeA.id)) {
				continue;
			}

			const boxA = getNodeBoundingBox(nodeA);

			// Skip nodes with invalid positions
			if (!boxA) {
				continue;
			}

			for (let j = i + 1; j < updatedNodes.length; j++) {
				const nodeB = updatedNodes[j];

				// Skip excluded nodes in inner loop as well
				if (excludeFromCollision?.has(nodeB.id)) {
					continue;
				}

				const boxB = getNodeBoundingBox(nodeB);

				// Skip nodes with invalid positions
				if (!boxB) {
					continue;
				}

				// Skip collision detection for directly connected nodes
				// They are expected to be vertically adjacent in the same flow
				if (areNodesConnected(nodeA.id, nodeB.id, connectedPairs)) {
					continue;
				}

				// Check for actual overlap (bounding boxes intersect)
				if (boxesOverlap(boxA, boxB)) {
					hasCollisions = true;

					// Calculate how much to push nodeB down to resolve collision
					const overlapY = boxA.bottom + collisionMargin - boxB.top;

					if (overlapY > 0) {
						// Push nodeB down
						updatedNodes[j] = {
							...nodeB,
							position: {
								x: nodeB.position.x,
								y: nodeB.position.y + overlapY,
							},
						};
					}
				}
			}
		}
	}

	if (iterations >= maxIterations) {
		console.warn('[layoutEngine] Max collision resolution iterations reached. Some overlaps may remain.');
	}

	return updatedNodes;
}
